import { OkThxWordmark } from "@/components/icons/Logo.tsx";
import { HeaderUtilitySlot } from "@/components/layout/HeaderUtilitySlot.tsx";
import { Omnibox } from "@/components/messages/Omnibox/Omnibox.tsx";
import { NEW_ROUTINE_PROMPT } from "@/views/Routines.tsx";
import { type ReactNode, useRef } from "react";
import { SplitPanelFrame } from "@/components/layout/SplitPanel/SplitPanelFrame.tsx";
import { SplitPanelHeader } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { UserTaskChips, useUserTaskChips } from "@/components/userTasks/UserTaskChips.tsx";
import { suggestionSlots, COMPACT_CHIP_BUDGET, ROOMY_CHIP_BUDGET } from "@/shared/userTaskChips.ts";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { type SuggestedPill, pillsToShow, SuggestedTaskPills } from "./SuggestedTaskPills.tsx";
import { useReportSuggestionsShown, useSuggestedTasks } from "./useSuggestedTasks.ts";

/**
 * What to offer when the catalog has nothing for us — the gate is closed or the
 * request failed. A page whose job is to show a new user what okthx can do
 * should never be emptier than it was before suggestions existed.
 */
const FALLBACK_PILLS: SuggestedPill[] = [
  {
    task: { id: "fallback-meeting", shortPrompt: "Schedule a meeting", examplePlatforms: [] },
    categoryId: "meetings",
  },
  {
    task: { id: "fallback-routine", shortPrompt: "Build me a routine", examplePlatforms: [] },
    categoryId: "organize",
    // The shared seed, so every routine entry point starts the user off the
    // same way. It ends mid-sentence on purpose: the cursor lands where the
    // detail goes.
    prefill: NEW_ROUTINE_PROMPT,
  },
];

/**
 * The compose surface itself: the brand mark, a greeting, the Omnibox, and
 * three suggested tasks beneath it. No chrome of its own — whoever renders it supplies that.
 *
 * One host at either width: {@link NewChatCanvasView}, a pivot the canvas
 * switches to, which is why this needs no scrim, no dismissal and no focus
 * trap. A phone's one column is exactly where its other destinations render, so
 * this is a screen there too rather than something laid over one.
 */
export const NewChatComposer = ({
  seededInput,
  onThreadCreated,
}: {
  /** Prefills the input, for the `?input=` deep link. */
  seededInput?: string;
  onThreadCreated: (threadId: number, background: boolean) => void;
}) => {
  const prefillRef = useRef<((text: string) => void) | null>(null);
  // The server draws a fresh sample per request and the query holds it still
  // while the page is open, so the three don't reshuffle under the cursor.
  const { data: suggestions, isLoading } = useSuggestedTasks();
  // The user's own tasks lead and take all but one of the screen's slots at
  // most; the suggestions fill whatever is left, so one is always on offer.
  // A narrow browser is a phone's screen and gets a phone's budget.
  const budget = useIsMobile() ? COMPACT_CHIP_BUDGET : ROOMY_CHIP_BUDGET;
  const { chips: taskChips, settled: chipsSettled } = useUserTaskChips(budget.tasks);
  // `isLoading` covers the suggestions' own fetch; the task list is the other
  // half, because the suggestions only get the slots the tasks leave.
  const pills = pillsToShow(
    suggestions?.categories ?? [],
    isLoading || !chipsSettled,
    FALLBACK_PILLS,
    suggestionSlots(budget, taskChips.length),
  );
  // Only drawn pills are reported; the fallback chips are not catalog tasks.
  useReportSuggestionsShown(
    suggestions?.personalized === true,
    suggestions ? pills.map((pill) => pill.task.id) : [],
  );

  return (
    // The gutter belongs to the host, not to this: the canvas view pads by
    // `--panel-gutter`, so an inset here would be the second one and leave the
    // composer narrower than the surface it fills.
    //
    // `my-auto` and not the host's `justify-center`: that centres a scroll
    // container by splitting its overflow across both ends, and the overflow
    // above the start edge cannot be scrolled back to — so a tall composer lost
    // its brand mark with no way to reach it. Auto margins collapse to zero once
    // the content no longer fits.
    <div className="my-auto flex w-full max-w-[688px] flex-col items-center gap-4">
      {/* Decorative brand mark; aria-hidden keeps its SVG <title> from
          announcing over the greeting, which is this surface's heading. */}
      <OkThxWordmark aria-hidden="true" className="h-7 w-auto" />
      <h1 className="mb-8 text-center font-serif text-3xl text-foreground">What can I take on?</h1>
      <Omnibox
        className="w-full"
        newThreadOnly
        // No `hostsPageEnrichments`: a new chat starts open-ended, not
        // pre-narrowed to whatever week the calendar happens to be showing.
        // Selection-scoped enrichments still reach it — pick a range on the grid
        // and its chip appears — because those pass regardless of the opt-in
        // (see `filterVisibleEnrichments`). Only the page-scoped implicit
        // view-week facet is suppressed.
        focusType="always"
        defaultValue={seededInput}
        prefillRef={prefillRef}
        onThreadCreated={onThreadCreated}
      />
      {/* One column at one gap, so the user's tasks and the prompt suggestions
          read as a single stack rather than two groups that happen to adjoin. */}
      <div className="flex w-full flex-col items-start gap-2">
        <UserTaskChips chips={taskChips} />
        <SuggestedTaskPills
          pills={pills}
          onSelect={(prompt) => prefillRef.current?.(prompt)}
          startIndex={taskChips.length}
        />
      </div>
    </div>
  );
};

/**
 * Composing, as a canvas view rather than a surface laid over one — so it
 * announces itself as a page, not a dialog, and the canvas has one source of
 * truth for what it is showing.
 *
 * No title in the header: the brand mark in the body is this page's title. No
 * floating new-chat bar either — the other views carry one to reach this page,
 * and a page does not need a button to itself.
 *
 * A phone renders this same view in its one column, with the nav menu still on
 * the bottom edge and the account chip still in the header — so the screen has
 * the same ways out as every other destination rather than a chevron of its own.
 */
export const NewChatCanvasView = ({
  seededInput,
  onThreadCreated,
  accountSwitcher,
  leading,
}: {
  seededInput?: string;
  onThreadCreated: (threadId: number, background: boolean) => void;
  accountSwitcher?: ReactNode;
  leading?: ReactNode;
}) => (
  <SplitPanelFrame
    header={
      <SplitPanelHeader>
        {leading}
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {accountSwitcher}
          {/* Ends the row here too, so the button holds the same corner on
              the new-chat surface as on every other screen. */}
          <HeaderUtilitySlot />
        </div>
      </SplitPanelHeader>
    }
  >
    {/* Sits just above dead centre. The suggestions hang below the input, so
        the block is tall enough that lifting it further — the third-of-the-way
        position the input used to hold alone — pushed the greeting up under
        the header.

        The bottom padding is what lifts it, so the block's middle lands at
        `(100 - 14) / 2` = 43vh. A proportion rather than a fixed offset, because
        the eye judges position relative to the window, which means something
        different on a laptop than on a tall display.

        The centring is `my-auto` on the child rather than `justify-center` here,
        and that distinction is load-bearing now that the composer can be tall.
        `justify-center` splits the overflow of a scroll container across both
        ends, and the overflow above the start edge cannot be scrolled back to —
        so on a short viewport the brand mark was cut off with no way to reach
        it. Auto margins collapse to zero once the content no longer fits, which
        centres a short composer identically and lets a tall one scroll from its
        real top. */}
    <div className="flex min-h-0 flex-1 flex-col items-center overflow-y-auto px-(--panel-gutter) pb-[14vh]">
      <NewChatComposer seededInput={seededInput} onThreadCreated={onThreadCreated} />
    </div>
  </SplitPanelFrame>
);
